import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, ShieldCheck, Sparkles, GraduationCap, Flower2, ArrowRight } from "lucide-react";
import SectionTitle from "../components/SectionTitle";
import FloatingPetals from "../components/FloatingPetals";
import Reveal from "../components/Reveal";
import BookLink from "../components/BookLink";
import useSeo from "../hooks/useSeo";
import { images, business } from "../data/content";

export default function About() {
  useSeo({
    title: `About | Soft Petal Electrolysis in ${business.city}, ${business.state}`,
    description: `Meet the founder of Soft Petal Electrolysis, a warm, inclusive electrolysis studio in ${business.city}, ${business.state}. Learn our story, mission, and commitment to gentle, personalized permanent hair removal.`,
    path: "/about",
    ogImage: images.founder,
  });

  const highlightWords = ["freedom", "confidence", "self-love", "kindness", "dignity", "genuine care"];

  const missionText =
    "At Soft Petal Electrolysis, we believe permanent hair removal is about more than smooth skin. It is about freedom, confidence, and self-love. We are dedicated to delivering exceptional electrolysis treatments in a warm, judgment-free environment where every client is treated with kindness, dignity, and genuine care.";

  const highlighted = missionText.split(/(freedom|confidence|self-love|kindness|dignity|genuine care)/gi).map((chunk, i) => {
    if (highlightWords.includes(chunk.toLowerCase())) {
      return (
        <em key={i} className="not-italic font-serif-display text-petal">
          {chunk}
        </em>
      );
    }
    return <React.Fragment key={i}>{chunk}</React.Fragment>;
  });

  const values = [
    { icon: Heart, title: "Inclusive care", copy: "Every skin tone, every hair texture, every story, welcomed with respect and joy." },
    { icon: ShieldCheck, title: "Sanitation & safety", copy: "Single-use, sterile probes and rigorous cleaning between every client." },
    { icon: Sparkles, title: "Personalized approach", copy: "Treatment plans crafted around your area, goals, and comfort." },
    { icon: GraduationCap, title: "Certified & trained", copy: "120-hour training in the Theory and Practice of Electrolysis at Berkowits School of Electrolysis, New York (March 2025)." },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="watercolor-blush py-16 lg:py-14 relative overflow-hidden">
        <FloatingPetals count={7} />
        <div className="max-w-5xl mx-auto px-6 sm:px-10 text-center relative">
          <SectionTitle
            eyebrow="About"
            title="The Heart Behind"
            scriptWord="Soft Petal Electrolysis"
            subtitle="A love letter, from our founder to every person who has ever wished someone truly understood."
          />
        </div>
      </section>

      {/* OUR STORY */}
      <section className="py-16 lg:py-14 relative" data-testid="our-story-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="rounded-[2.5rem] overflow-hidden aspect-[4/5] shadow-soft">
              <img
                src={images.founder}
                alt="Our founder holding an electrolysis anatomy model at her certification"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-6 font-script text-petal text-3xl text-center">With love,</div>
            <div className="text-center font-serif-display text-charcoal">Soft Petal Electrolysis</div>
          </div>

          <div className="lg:col-span-7 space-y-6 font-body text-charcoal leading-relaxed text-lg">
            <p className="font-serif-display italic text-2xl text-charcoal">
              &ldquo;One of the questions I am asked most often is, &lsquo;Why did you become an electrologist?&rsquo; My answer is a personal one.&rdquo;
            </p>
            <p>
              I have <span className="text-petal font-medium">PMOS</span>, previously known as <span className="text-petal font-medium">PCOS</span>, and I understand what it feels like to look in the mirror and not recognize the person staring back. I know what it is like to avoid bright lighting, hesitate before hugging someone, or skip special occasions because you do not feel like yourself.
            </p>
            <p>
              <span className="text-petal font-medium">When I began my own electrolysis journey, everything changed.</span> I stopped hiding. I began saying yes to photographs, plans, and the little moments I had once avoided. For the first time in a long time, I felt free and like myself again.
            </p>
            <p>
              <span className="text-petal font-medium">That transformation inspired me to become an electrologist.</span> I wanted to learn everything I could about this life-changing treatment so I could help someone else experience that same freedom, confidence, and joy.
            </p>
            <p>
              Representation also matters. I did not see many electrologists in my area who looked like me, and I wanted to become the person I had once searched for.
            </p>
            <p>
              Soft Petal Electrolysis was created as a place where every person, regardless of background, skin tone, or story, can feel welcomed, seen, and genuinely cared for.
            </p>
            <div className="mt-10 border-l-4 border-petal pl-6 py-3 bg-blush/40 rounded-r-2xl">
              <p className="font-serif-display italic text-xl text-charcoal leading-snug">
                This has never been only about removing hair. It has always been about helping people rediscover themselves, reclaim their confidence, and bloom into the person they deserve to be.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-16 lg:py-14 watercolor-blush relative overflow-hidden" data-testid="mission-section">
        <FloatingPetals count={6} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="Our Mission"
            title="More than smooth skin ,"
            scriptWord="a place to bloom."
          />
          <p className="mt-10 font-serif-display text-2xl sm:text-[1.75rem] leading-relaxed text-charcoal">
            {highlighted}
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-2">
            {highlightWords.map((w, i) => (
              <span
                key={w}
                className="font-script text-petal text-2xl px-3 float-gentle"
                style={{ animationDelay: `${i * 0.4}s`, animationDuration: `${6 + (i % 3)}s` }}
                data-testid={`mission-word-${w.replace(/\s+/g, "-")}`}
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-16 lg:py-14">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <SectionTitle
            eyebrow="What Guides Us"
            title="Care that feels"
            scriptWord="soft & certain"
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal
                  key={v.title}
                  delay={i * 110}
                  className="bg-warm-white rounded-3xl p-8 border border-pale-rose/50 shadow-soft lift-on-hover"
                  data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-12 h-12 rounded-full bg-blush flex items-center justify-center">
                    <Icon className="text-petal" size={20} />
                  </div>
                  <h3 className="mt-5 font-serif-display text-xl leading-tight">{v.title}</h3>
                  <p className="mt-2 font-body text-sm text-charcoal-soft leading-relaxed">
                    {v.copy}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CREDENTIALS + DIPLOMA */}
      <section className="py-16 lg:py-20 bg-blush/40" data-testid="credentials-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 text-petal font-body text-xs tracking-[0.3em] uppercase">
              <Flower2 size={14} /> Certification
            </div>
            <h3 className="font-serif-display text-3xl sm:text-4xl mt-4 leading-tight">
              Trained at Berkowits School of{" "}
              <span className="font-script text-petal text-[1.15em]">Electrolysis</span>
            </h3>
            <ul className="mt-8 space-y-4 font-body text-charcoal-soft leading-relaxed">
              <li className="flex items-start gap-3">
                <GraduationCap size={18} className="text-petal mt-1 shrink-0" />
                <div>
                  <div className="text-charcoal font-medium">Genesis Amilion Ellerbe</div>
                  <div className="text-sm">Certified Electrologist</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-petal mt-1 shrink-0" />
                <div>
                  <div className="text-charcoal font-medium">
                    120 hours in the Theory and Practice of Electrolysis
                  </div>
                  <div className="text-sm">
                    Berkowits School of Electrolysis, Forest Hills, New York (March 2025)
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Heart size={18} className="text-petal fill-petal mt-1 shrink-0" />
                <div>
                  <div className="text-charcoal font-medium">
                    Sanitation & infection control
                  </div>
                  <div className="text-sm">
                    Single-use, sterile probes for every client. Strict disinfection standards
                    between every session.
                  </div>
                </div>
              </li>
            </ul>
          </Reveal>

          <Reveal delay={140} className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden bg-warm-white shadow-soft border border-pale-rose/40 p-4 sm:p-6">
                <img
                  src={images.diploma}
                  alt="Berkowits School of Electrolysis certificate awarded to Genesis Amilion Ellerbe, March 2025"
                  loading="lazy"
                  className="w-full h-auto rounded-xl block"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 w-24 h-24 rounded-full bg-blush flex items-center justify-center shadow-soft float-gentle">
                <div className="text-center">
                  <div className="font-script text-petal text-xl leading-none">Class</div>
                  <div className="font-script text-petal text-xl leading-none">of 2025</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionTitle title="Come as you are." scriptWord="Leave feeling yourself." />
          <div className="mt-10">
            <BookLink  className="btn-petal" data-testid="about-cta-book">
              Book Your Consultation
              <ArrowRight size={14} />
            </BookLink>
          </div>
        </div>
      </section>
    </div>
  );
}
